import { Extension } from "@tiptap/core";

type BlockIndentOptions = {
  types: string[];
  listTypes: string[];
};

export const BLOCK_INDENT_TYPES = ["paragraph"];

/** Lists indent as a whole, bullets included; their items nest instead where they can. */
export const LIST_INDENT_TYPES = ["bulletList", "orderedList", "taskList"];

/** Keep in sync with the `ps-*` / `ms-*` classes listed by `@source inline` in the web app's `theme.css`. */
export const MAX_BLOCK_INDENT = 20;

/**
 * Width of one indent step in Tailwind spacing units (8 × 0.25rem = 2rem), for both `indent`
 * levels and the first-line indent.
 */
export const BLOCK_INDENT_STEP = 8;

function parseIndent(element: HTMLElement): number {
  const indent = Number(element.dataset.indent);
  return Number.isInteger(indent) ? Math.min(Math.max(indent, 0), MAX_BLOCK_INDENT) : 0;
}

/** `prefix`: the Tailwind spacing utility, padding for paragraphs and margin for lists. */
function indentAttribute(prefix: "ps" | "ms") {
  return {
    default: 0,
    parseHTML: parseIndent,
    renderHTML: ({ indent }: { indent?: number }) => {
      if (!indent) {
        return null;
      }

      return {
        "data-indent": indent,
        class: `${prefix}-${indent * BLOCK_INDENT_STEP}`,
      };
    },
  };
}

/**
 * Google Docs-style indentation: `indent` shifts the whole block by that many steps, and a
 * paragraph's `firstLineIndent` additionally shifts its first line by one step. Lists take the
 * margin, since their own padding holds the bullets.
 */
export const BlockIndent = Extension.create<BlockIndentOptions>({
  name: "blockIndent",

  addOptions() {
    return {
      types: BLOCK_INDENT_TYPES,
      listTypes: LIST_INDENT_TYPES,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: indentAttribute("ps"),
          firstLineIndent: {
            default: false,
            parseHTML: (element) => element.hasAttribute("data-first-line-indent"),
            renderHTML: ({ firstLineIndent }: { firstLineIndent?: boolean }) => {
              if (!firstLineIndent) {
                return null;
              }

              return {
                "data-first-line-indent": "",
                class: `indent-${BLOCK_INDENT_STEP}`,
              };
            },
          },
        },
      },
      {
        types: this.options.listTypes,
        attributes: {
          indent: indentAttribute("ms"),
        },
      },
    ];
  },
});
